import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import vendorUser from "../../assets/images/vendorLogo.svg";
import { crossIcon, expireIcon, tickIcon } from "../../assets/uppoIcons/icons";
import { AuthContext } from "../../components/auth/context/Context";
import ReactPagination from "../../components/pagination/Pagination";
import { ThemeConfig } from "../../dataTypes/ThemeConfig";

interface VendorBenefits {
  id: number;
  path: string;
  title: string;
  uses: number;
  created_at: any;
  deleted_at: any;
  expires_at: any;
}

interface VendorBenefitsProps {
  themeConfig: ThemeConfig;
}

export const VendorBenefits: React.FC<VendorBenefitsProps> = ({
  themeConfig,
}) => {
  const { slug, id } = useParams<{ slug: string; id: string }>();
  const { setBenefitsTag } = useContext(AuthContext);
  const location = useLocation();
  const state = location.state as { name?: string; path?: string };
  const { name, path } = state || {};
  const navigate = useNavigate();
  const [vendorBenefits, setVendorBenefits] = useState<VendorBenefits[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);

  const handleImgError = (e: any) => {
    e.target.src = vendorUser;
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage - 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const paginatedVendorBenefits = vendorBenefits.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getStatusTag = (item: VendorBenefits) => {
    const now = moment();
    const expiresAt = item.expires_at ? moment(item.expires_at) : null;
    const isExpired = expiresAt && expiresAt.isBefore(now);
    const isDeleted = item.deleted_at !== null;

    if (isDeleted && isExpired) {
      return [
        {
          label: (
            <span style={{ display: "flex", alignItems: "center", gap: "2px" }}>
              {expireIcon} <span style={{ paddingTop: "2px" }}>Expirada</span>
            </span>
          ),
          color: "#D7D7D7",
        },
        {
          label: (
            <span style={{ display: "flex", alignItems: "center", gap: "2px" }}>
              {crossIcon} <span style={{ paddingTop: "2px" }}>Inativa</span>
            </span>
          ),
          color: "#FF5C5C",
        },
      ];
    }

    if (isDeleted) {
      return [
        {
          label: (
            <span style={{ display: "flex", alignItems: "center", gap: "2px" }}>
              {crossIcon} <span style={{ paddingTop: "2px" }}>Inativa</span>
            </span>
          ),
          color: "#FF5C5C",
        },
      ];
    }

    if (isExpired) {
      return [
        {
          label: (
            <span style={{ display: "flex", alignItems: "center", gap: "2px" }}>
              {expireIcon} <span style={{ paddingTop: "2px" }}>Expirada</span>
            </span>
          ),
          color: "#D7D7D7",
        },
      ];
    }

    return [
      {
        label: (
          <span style={{ display: "flex", alignItems: "center", gap: "2px" }}>
            {tickIcon} <span style={{ paddingTop: "2px" }}>Ativa</span>
          </span>
        ),
        color: "#2AD200",
      },
    ];
  };

  useEffect(() => {
    const fetchVendorBenefits = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No token found");
        }

        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/${slug}/vendorUser/getVendorBenefits/${id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch vendor benefits");
        }
        const data = await response.json();
        setVendorBenefits(data.data);
      } catch (error) {
        console.error("Error fetching vendor benefits:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVendorBenefits();
  }, [slug, id]);

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        margin: "40px auto ",
        display: "flex",
        flexDirection: "column",
        maxHeight: 1000,
        width: 918,
        justifyContent: "space-between",
      }}
    >
      <Box
        maxHeight="800px"
        overflow="auto"
        width={918}
        padding="23px 148px"
        sx={{
          bgcolor: "#fff",
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "column",
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "11px",
              alignItems: "center",
              paddingBottom: "22px",
            }}
          >
            <img
              src={path}
              alt=""
              style={{ width: "100px", margin: "0px" }}
              onError={handleImgError}
            />
            <Typography sx={{ fontSize: "25px", fontWeight: 500 }}>
              {name}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "25px", fontWeight: 700 }}>
              Parcerias
            </Typography>
          </Box>

          <Box>
            {loading ? (
              <CircularProgress
                style={{
                  display: "flex",
                  justifyContent: "center",
                  margin: "auto",
                }}
              />
            ) : vendorBenefits.length === 0 ? (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 500, textAlign: "center" }}
              >
                No Data Found
              </Typography>
            ) : (
              paginatedVendorBenefits.map((item: VendorBenefits) => {
                const statusTag = getStatusTag(item);
                return (
                  <Box
                    key={item.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      borderTop: "1px solid #E0E0E0",
                      padding: "12px 0",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      navigate(`/${slug}/${id}/benefits/${item?.id}`, {
                        state: { name: item.title, path: item.path },
                      });
                      setBenefitsTag([...statusTag]);
                    }}
                  >
                    <Box sx={{ margin: "12px 21px 12px 7px " }}>
                      <img
                        src={item.path}
                        style={{
                          marginBottom: "0px",
                          width: "170px",
                          height: "100px",
                          objectFit: "contain",
                        }}
                        onError={handleImgError}
                        alt=""
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Box sx={{ width: "60%" }}>
                        <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: 500,
                            marginBottom: "10px",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            navigate(`/${slug}/${id}/benefits/${item?.id}`, {
                              state: { name: item.title, path: item.path },
                            });
                            setBenefitsTag([...statusTag]);
                          }}
                        >
                          Utilizada {item.uses}x
                        </Typography>
                        <Box sx={{ display: "flex", gap: "8px" }}>
                          {" "}
                          {statusTag.map((label: any) => {
                            return (
                              <Chip
                                // color="primary"
                                sx={{
                                  height: "28px",
                                  bgcolor: label.color,
                                  borderRadius: "18px",
                                  padding: "1px 8px",
                                  // color: "black",
                                  textTransform: "capitalize",
                                  display: "flex",
                                  gap: "4px",
                                  alignItems: "center",
                                  width: "max-content",
                                  "& .MuiChip-label": {
                                    fontSize: "15px",
                                    fontWeight: "medium",
                                    padding: "0px 8px",
                                  },
                                }}
                                label={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: "4px",
                                      alignItems: "center",
                                    }}
                                  >
                                    {label.label}
                                  </Box>
                                }
                              />
                            );
                          })}
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography sx={{ fontWeight: 500 }}>
                          {item.expires_at
                            ? `Expira: ${moment(item.expires_at).format(
                                "DD/MM/YYYY"
                              )}`
                            : "Nunca expira"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Box>
      {vendorBenefits.length > rowsPerPage && (
        <ReactPagination
          page={page + 1}
          rowsPerPage={rowsPerPage}
          count={vendorBenefits.length}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Box>
  );
};
